"use client";

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "@/redux/rootReducer";

export interface UserIdentityMeta {
    id?: string;
    email?: string;
    name?: string;
    surname?: string;
    roles?: string[];
    establishmentId?: string;
    type?: string;
}

export interface RouteEntityIds {
    patientId?: string;
    consultationId?: string;
    reportId?: string;
    formSubmissionId?: string;
}

export interface UserBusinessMeta {
    route: {
        path: string;
        segments: string[];
        query: Record<string, string>;
        entities: RouteEntityIds;
    };
    user?: UserIdentityMeta;
    uiState?: {
        modalOpen?: boolean;
        modalLabel?: string | null;
        activeTab?: string | null;
        wizardStep?: string | number | null;
    };
    focus?: {
        role?: string | null;
        name?: string | null;
        dataset?: Record<string, string>;
    };
    selection?: {
        text?: string | null;
        length?: number;
    };
    kpis?: Array<{ key: string; value: string }>;
    attachments?: string[];
    selectedRowIds?: string[];
    contextCards?: Array<{ key: string; kind?: string; payload?: unknown }>;
}

const SENSITIVE_QS = new Set(["authorization", "Authorization", "token", "access_token", "message"]);

function parseQuery(search: string): Record<string, string> {
    const out: Record<string, string> = {};
    const qs = new URLSearchParams(search || "");
    qs.forEach((value, key) => {
        if (!SENSITIVE_QS.has(key)) out[key] = value;
    });
    return out;
}

function extractEntities(segments: string[]): RouteEntityIds {
    const ids: RouteEntityIds = {};
    for (let i = 0; i < segments.length; i++) {
        const s = segments[i];
        const next = segments[i + 1];
        if (!next) continue;
        if (s === "patients" || s === "patient") ids.patientId = next;
        if (s === "consultations" || s === "consultation") ids.consultationId = next;
        if (s === "reports" || s === "report") ids.reportId = next;
        if (s === "form-submissions" || s === "forms") ids.formSubmissionId = next;
    }
    return ids;
}

/**
 * Collects business metadata: current route, sanitized query, inferred entity IDs, and user identity.
 * Does NOT expose tokens or sensitive query parameters.
 */
export function useUserBusinessMeta(): UserBusinessMeta {
    const location = useLocation();

    // Try common auth user shapes; keep it defensive to avoid tight coupling
    const rawAuth = useAppSelector((state) => (state as any)?.auth);
    const authUser = rawAuth?.user ?? rawAuth?.profile ?? null;

    const user: UserIdentityMeta | undefined = useMemo(() => {
        if (!authUser) return undefined;
        return {
            id: authUser._id || authUser.id,
            email: authUser.email,
            name: authUser.userInfo?.name || authUser.name,
            surname: authUser.userInfo?.surname || authUser.surname,
            roles: Array.isArray(authUser.roles) ? authUser.roles : undefined,
            establishmentId: authUser.establishment?._id || authUser.establishmentId,
            type: authUser.type,
        };
    }, [authUser]);

    const route = useMemo(() => {
        const path = location.pathname || "/";
        const segments = path.split("/").filter(Boolean);
        const query = parseQuery(location.search);
        const entities = extractEntities(segments);
        return { path, segments, query, entities };
    }, [location.pathname, location.search]);

    // Focus metadata (no input values)
    const [focus, setFocus] = useState<UserBusinessMeta["focus"]>();
    useEffect(() => {
        const handler = () => {
            if (typeof document === "undefined") return;
            const el = document.activeElement as HTMLElement | null;
            if (!el) { setFocus(undefined); return; }
            const role = el.getAttribute("role");
            const name = el.getAttribute("aria-label") || el.getAttribute("aria-labelledby") || el.getAttribute("title") || null;
            const dataset: Record<string, string> = {};
            Object.keys((el as any).dataset || {}).forEach((k) => {
                if (!SENSITIVE_QS.has(k)) dataset[k] = String((el as any).dataset[k]);
            });
            setFocus({ role: role || null, name, dataset: Object.keys(dataset).length ? dataset : undefined });
        };
        window.addEventListener("focusin", handler);
        handler();
        return () => window.removeEventListener("focusin", handler);
    }, []);

    // Selected text (truncated)
    const [selection, setSelection] = useState<UserBusinessMeta["selection"]>({ text: null, length: 0 });
    useEffect(() => {
        const onSel = () => {
            try {
                const sel = window.getSelection?.();
                const text = sel && sel.toString ? sel.toString() : "";
                const trimmed = text.trim();
                const truncated = trimmed.length > 200 ? trimmed.slice(0, 200) + "…" : trimmed;
                setSelection({ text: truncated || null, length: trimmed.length || 0 });
            } catch { }
        };
        document.addEventListener("selectionchange", onSel);
        return () => document.removeEventListener("selectionchange", onSel);
    }, []);

    // UI state quick scan (modal/tab/step)
    const uiState = useMemo<UserBusinessMeta["uiState"]>(() => {
        if (typeof document === "undefined") return {};
        const dialog = document.querySelector('[role="dialog"], .MuiDialog-root') as HTMLElement | null;
        const modalOpen = !!dialog;
        let modalLabel: string | null = null;
        if (dialog) {
            modalLabel = dialog.getAttribute("aria-label") || dialog.getAttribute("aria-labelledby") || null;
        }
        const activeTabEl = document.querySelector('[data-active-tab="true"], [aria-selected="true"][role="tab"]') as HTMLElement | null;
        const activeTab = activeTabEl?.getAttribute("data-tab-key") || activeTabEl?.textContent?.trim() || null;
        const stepEl = document.querySelector('[aria-current="step"], [data-step-current="true"]') as HTMLElement | null;
        const wizardStep = stepEl?.getAttribute("data-step") || stepEl?.textContent?.trim() || null;
        return { modalOpen, modalLabel, activeTab, wizardStep };
    }, [location.pathname, location.search]);

    // Lightweight page signals: KPIs, attachments, selected rows, context cards
    const { kpis, attachments, selectedRowIds, contextCards } = useMemo(() => {
        if (typeof document === "undefined") return { kpis: [], attachments: [], selectedRowIds: [], contextCards: [] };

        const capText = (s: string | null | undefined, max = 60) => {
            const t = (s || "").trim();
            return t.length > max ? t.slice(0, max) + "…" : t;
        };

        const kpiEls = Array.from(document.querySelectorAll('[data-kpi]')) as HTMLElement[];
        const kpis = kpiEls.slice(0, 5).map((el, idx) => ({
            key: el.getAttribute('data-kpi') || el.getAttribute('data-kpi-key') || `kpi_${idx}`,
            value: capText(el.textContent)
        }));

        const attEls = Array.from(document.querySelectorAll('[data-attachment-key]')) as HTMLElement[];
        const attachments = attEls.slice(0, 5).map(el => el.getAttribute('data-attachment-key') || '').filter(Boolean) as string[];

        const rowEls = Array.from(document.querySelectorAll('[role="row"][aria-selected="true"], [data-selected="true"]')) as HTMLElement[];
        const selectedRowIds = rowEls.slice(0, 5).map(el => el.getAttribute('data-id') || el.getAttribute('data-row-id') || '').filter(Boolean) as string[];

        const rawCards: any[] = (window as any)?.MC_AGENT_CONTEXT_CARDS;
        const contextCards = Array.isArray(rawCards)
            ? rawCards.slice(0, 10).map((c, i) => {
                const key = c?.key || `card_${i}`;
                const kind = c?.kind;
                let payload: unknown = undefined;
                try {
                    const str = JSON.stringify(c?.payload ?? {});
                    payload = str.length > 1200 ? JSON.parse(str.slice(0, 1200)) : c?.payload;
                } catch { }
                return { key, kind, payload };
            })
            : [];

        return { kpis, attachments, selectedRowIds, contextCards };
    }, [location.pathname, location.search]);

    return { route, user, uiState, focus, selection, kpis, attachments, selectedRowIds, contextCards };
}

export default useUserBusinessMeta;

